import { useMergeRefs } from '@floating-ui/react'
import { css, cx } from '@linaria/core'
import { type FC, type HTMLProps, type ReactNode, useRef } from 'react'
import CheckIcon from '#icons/check-24.svg'
import MinusIcon from '#icons/minus-24.svg'
import { fragments, style } from '#styles/index'

const labelCheckboxClass = css`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;

  > .label {
    cursor: pointer;
    display: flex;
    align-items: center;
    color: ${style.theme.textDefault};
    ${fragments.textStyle.body.s.regular};
    
    &.disabled {
      color: ${style.theme.textDisabled}
    }
  }
`

const checkboxClass = css`
  cursor: pointer;
  appearance: none;
  position: relative;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  max-height: 16px;
  max-width: 16px;

  transition: 0.25s ease outline-color, 0.25s ease border-color, 0.25s ease background-color;
  
  border: 1px solid ${style.theme.borderInput};
  border-radius: none;
  outline-color: transparent;

  &:hover {
    border-color: ${style.theme.borderHover};
  }

  &:focus {
    outline: 2px solid ${style.theme.borderOutline};
    outline-offset: 0;
    border-color: ${style.theme.borderSurface};
  }

  &:before {
    /* area */
    transition: 0.25s ease background-color;
    content: " ";
    display: block;
    position: absolute;
    background-color: transparent;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }


  &:checked { 
    &:before {
      mask: url(${CheckIcon}) center/contain no-repeat,
      // color is irrelevant, only required to fill the space for the mask to apply correctly
      linear-gradient(black 0 0);
      mask-composite: exclude;
      background-color: ${style.theme.backgroundSurfaceAccentDefault};
    }
    
    /* checkmark */
    background-color: ${style.theme.textWhite};
    border-color: ${style.theme.textAccent};

    &:focus {
      outline: 2px solid ${style.theme.borderOutline};
      outline-offset: 0;
    }

    &:hover {
      /* checkmark */
      background-color: ${style.theme.textWhite};
      border-color: ${style.theme.borderHover};

      &:before {
        /* area */
        background-color: ${style.theme.backgroundSurfaceAccentStrong};
      }
    }
  }

  &.intermediate, 
  &.intermediate:disabled {
    /* checkmark */
    mask: url(${MinusIcon}) center/contain no-repeat,
    // color is irrelevant, only required to fill the space for the mask to apply correctly
    linear-gradient(black 0 0);
    mask-size: 90% 90%;
    mask-composite: exclude;
    background-color: ${style.theme.backgroundSurfaceAccentDefault};

    &:disabled {
      background-color: ${style.theme.backgroundSurfaceDisabled};
    }

    &:hover {
      background-color: ${style.theme.backgroundSurfaceAccentStrong};   
    }
  }

  &.invalid, &.invalid:disabled {
    border-color: ${style.theme.borderDanger};

  }
  
  &.invalid {
    background-color: ${style.theme.backgroundSemanticDanger};
    
    &:before {
      mask: none;
      background-color: ${style.theme.backgroundSemanticDanger};
    }

    &:hover {
      border-color: ${style.theme.borderInput};
    }
    
    &:disabled {
      &:before {
        background-color: ${style.theme.backgroundSurfaceDisabled};
      }

      &:hover {
        border-color: ${style.theme.borderDanger};
      }
    }

  }

  
  &:disabled {
    cursor: text;
    /* checkmark */
    border-color: ${style.theme.textDisabled};
    background: ${style.theme.textDisabled};
    
    &:before {
      /* area */
      background-color: ${style.theme.backgroundSurfaceDisabled};
    }

    &:hover :before {
      /* area */
      background-color: ${style.theme.backgroundSurfaceTertiary};
    }
  }  
`

export const Checkbox: FC<
  {
    label?: ReactNode
    type?: 'checkbox'
    kind?: 'default' | 'intermediate' | 'invalid'
    clickableLabel?: boolean
  } & Omit<HTMLProps<HTMLInputElement>, 'label'>
> = ({ ref, label, className, kind = 'default', disabled, clickableLabel = true, ...props }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const mergedRefs = useMergeRefs([inputRef, ref])
  if (label) {
    return (
      <div className={cx(labelCheckboxClass, className, '_checkbox')}>
        <input
          ref={mergedRefs}
          type="checkbox"
          className={cx(checkboxClass, 'checkbox', kind !== 'default' && kind, className)}
          disabled={disabled}
          {...props}
        />
        <label
          className={cx('label', disabled && 'disabled')}
          onClick={() => {
            inputRef.current?.click()
          }}
        >
          {label}
        </label>
      </div>
    )
  }
  return (
    <input
      ref={ref}
      type="checkbox"
      className={cx(checkboxClass, 'checkbox', kind !== 'default' && kind, className)}
      disabled={disabled}
      {...props}
    />
  )
}
